import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Operations from './pages/Operations';
import { SnackBarHome, SnackBarKDS, SnackBarDisplay, SnackBarAdmin, SnackBarReports, GuestOrder, SnackBarPOS } from './pages/SnackBar';
import { SnackBarTv } from './pages/SnackBarTv';
import { GuestMenuLanding } from './pages/GuestMenuLanding';
import Assets from './pages/Assets';
import AssetForm from './pages/AssetForm';
import Companies from './pages/Companies';
import CompanyForm from './pages/CompanyForm';
import Notifications from './pages/Notifications';
import BarStock from './pages/BarStock';
import Complimentary from './pages/Complimentary';
import ComplimentaryProducts from './pages/ComplimentaryProducts';
import ComplimentaryPersonnel from './pages/ComplimentaryPersonnel';
import Cameras from './pages/Cameras';
import AccessPoints from './pages/AccessPoints';
import LocalPCs from './pages/LocalPCs';
import Passwords from './pages/Passwords';
import Products from './pages/Products';
import UserManagement from './pages/UserManagement';
import Reports from './pages/Reports';
import Backup from './pages/Backup';
import Depo from './pages/Depo';
import Sayim from './pages/Sayim';
import Talepler from './pages/Talepler';
import GarsonEntry from './pages/GarsonEntry';
import Settings from './pages/Settings';
import Social from './pages/Social';
import SocialMediaAdmin from './pages/SocialMediaAdmin';
import Minibar from './pages/Minibar';
import MinibarProducts from './pages/MinibarProducts';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Keep-alive: ping backend every 2 min while tab is open
function useKeepAlive() {
    useEffect(() => {
        // Ping every 60 seconds to prevent platform sleep
        const ping = () => fetch(`${API_URL}/api/health`).catch(() => {});
        ping(); // immediate ping
        const interval = setInterval(ping, 60000); // 1 minute
        
        // Also ping when tab becomes visible again
        const onVisibility = () => { if (document.visibilityState === 'visible') ping(); };
        document.addEventListener('visibilitychange', onVisibility);
        
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);
}

function ProtectedRoute({ children }) {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orwion-green"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
    const { isAuthenticated, loading, isGarson, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orwion-green"></div>
            </div>
        );
    }

    if (isAuthenticated) {
        if (isGarson) {
            return <Navigate to="/garson-entry" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/social" element={<Social />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/operations" element={<ProtectedRoute><Operations /></ProtectedRoute>} />
            <Route path="/operations/:dep" element={<ProtectedRoute><Operations /></ProtectedRoute>} />
            <Route path="/snack-bar" element={<ProtectedRoute><SnackBarPOS /></ProtectedRoute>} />
            <Route path="/snack-bar/home" element={<ProtectedRoute><SnackBarHome /></ProtectedRoute>} />
            <Route path="/snack-bar/kitchen" element={<ProtectedRoute><SnackBarKDS station="kitchen" /></ProtectedRoute>} />
            <Route path="/snack-bar/bar-pos" element={<ProtectedRoute><SnackBarKDS station="bar" /></ProtectedRoute>} />
            <Route path="/snack-bar/display" element={<ProtectedRoute><SnackBarDisplay /></ProtectedRoute>} />
            <Route path="/snack-bar/tv" element={<SnackBarTv />} />
            <Route path="/menu" element={<GuestMenuLanding />} />
            <Route path="/snack-bar/menu-admin" element={<ProtectedRoute><SnackBarAdmin /></ProtectedRoute>} />
            <Route path="/snack-bar/reports" element={<ProtectedRoute><SnackBarReports /></ProtectedRoute>} />
            <Route path="/order" element={<GuestOrder />} />
            
            {/* IT Danışmanlık */}
            <Route path="/assets" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
            <Route path="/assets/new" element={<ProtectedRoute><AssetForm /></ProtectedRoute>} />
            <Route path="/assets/:id" element={<ProtectedRoute><AssetForm /></ProtectedRoute>} />
            
            <Route path="/cameras" element={<ProtectedRoute><Cameras /></ProtectedRoute>} />
            <Route path="/access-points" element={<ProtectedRoute><AccessPoints /></ProtectedRoute>} />
            <Route path="/local-pcs" element={<ProtectedRoute><LocalPCs /></ProtectedRoute>} />
            <Route path="/passwords" element={<ProtectedRoute><Passwords /></ProtectedRoute>} />
            
            <Route path="/companies" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
            <Route path="/companies/new" element={<ProtectedRoute><CompanyForm /></ProtectedRoute>} />
            <Route path="/companies/:id" element={<ProtectedRoute><CompanyForm /></ProtectedRoute>} />
            
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            
            {/* Products (Ürünler) */}
            <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
            
            {/* Bar Stok Routes */}
            <Route path="/bar/:barId" element={<ProtectedRoute><BarStock /></ProtectedRoute>} />
            <Route path="/minibar" element={<ProtectedRoute><Minibar /></ProtectedRoute>} />
            <Route path="/minibar/products" element={<ProtectedRoute><MinibarProducts /></ProtectedRoute>} />
            <Route path="/complimentary" element={<ProtectedRoute><Complimentary /></ProtectedRoute>} />
            <Route path="/odenmez" element={<ProtectedRoute><Complimentary /></ProtectedRoute>} />
            <Route path="/odenmez/products" element={<ProtectedRoute><ComplimentaryProducts /></ProtectedRoute>} />
            <Route path="/odenmez/personnel" element={<ProtectedRoute><ComplimentaryPersonnel /></ProtectedRoute>} />
            <Route path="/garson-entry" element={<ProtectedRoute><GarsonEntry /></ProtectedRoute>} />
            
            {/* Stok İşlemleri Routes */}
            <Route path="/stock/talepler" element={<ProtectedRoute><Talepler /></ProtectedRoute>} />
            <Route path="/stock/depo" element={<ProtectedRoute><Depo /></ProtectedRoute>} />
            <Route path="/stock/sayim" element={<ProtectedRoute><Sayim /></ProtectedRoute>} />
            
            {/* Yönetim Routes */}
            <Route path="/management/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
            <Route path="/management/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/management/backup" element={<ProtectedRoute><Backup /></ProtectedRoute>} />
            <Route path="/management/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/management/social" element={<ProtectedRoute><SocialMediaAdmin /></ProtectedRoute>} />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    useKeepAlive();
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
